<template>
    <v-dialog persistent :value="value" max-width="480" @input="onDialogInput">
        <v-card class="pa-3">
            <v-card-title class="headline pl-0 pt-0">{{ title }}</v-card-title>

            <v-layout row align-center>
                <v-text-field
                    v-model="newValue"
                    label="New Type"
                    :rules="[rules.required]"
                    ref="newValue"
                    @keyup.enter.native="addValue"
                ></v-text-field>
                <v-btn
                    small
                    color="success"
                    class="ml-2 mb-3"
                    :loading="processing"
                    @click="addValue"
                    >Add</v-btn
                >
            </v-layout>

            <div v-if="loading" class="grey--text mb-2">Loading...</div>
            <div v-else-if="!options.length" class="grey--text mb-2">
                No types yet.
            </div>

            <div v-for="(option, index) in options" :key="option.id || index">
                <v-layout
                    v-if="option.confirmingDelete"
                    row
                    align-center
                    class="py-1"
                >
                    <div>Remove {{ option.value }}?</div>
                    <v-spacer></v-spacer>
                    <v-btn
                        small
                        dark
                        color="default"
                        class="mr-1"
                        @click="option.confirmingDelete = false"
                        >No</v-btn
                    >
                    <v-btn
                        small
                        color="success"
                        :loading="processing"
                        @click="deleteOption(option)"
                        >Yes</v-btn
                    >
                </v-layout>
                <v-layout
                    v-else-if="!option.editing"
                    row
                    align-center
                    class="py-1"
                >
                    <div>{{ option.value }}</div>
                    <v-spacer></v-spacer>
                    <v-btn
                        flat
                        icon
                        color="success"
                        class="my-0"
                        @click="editOption(option)"
                    >
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                        flat
                        icon
                        color="#f55a4e"
                        class="my-0"
                        @click="option.confirmingDelete = true"
                    >
                        <v-icon>mdi-delete</v-icon>
                    </v-btn>
                </v-layout>
                <v-layout v-else row align-center>
                    <v-text-field
                        v-model="option.value"
                        label="Value"
                        :rules="[rules.required]"
                        ref="optionValue"
                    ></v-text-field>
                    <v-text-field
                        class="ml-2"
                        style="max-width: 90px;"
                        v-model="option.sortOrder"
                        label="Sort"
                        type="number"
                    ></v-text-field>
                    <v-btn
                        small
                        dark
                        color="default"
                        class="ml-2"
                        @click="cancelEdit"
                        >Cancel</v-btn
                    >
                    <v-btn
                        small
                        color="success"
                        :loading="processing"
                        @click="saveOption(option)"
                        >Save</v-btn
                    >
                </v-layout>
                <v-divider
                    v-if="index + 1 < options.length"
                    class="my-1"
                ></v-divider>
            </div>

            <v-card-actions class="px-0 pb-0 mt-3 mr-0">
                <v-spacer></v-spacer>
                <v-btn dark color="default" @click="close">Close</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
export default {
    props: {
        value: Boolean,
        lookupType: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            default: "Lookup Values",
        },
    },
    data: () => ({
        options: [],
        newValue: "",
        loading: false,
        processing: false,
        rules: {
            required: (value) => !!((value || "").trim()) || "Required.",
        },
    }),
    watch: {
        value(open) {
            if (open) {
                this.newValue = "";
                this.load();
            }
        },
    },
    methods: {
        onDialogInput(open) {
            if (!open) {
                this.close();
            }
        },
        close() {
            this.$emit("input", false);
        },
        async load() {
            this.loading = true;

            try {
                const response = await this.$http.get(
                    "/odata/smlookup?$filter=contains(type, '" +
                        this.lookupType +
                        "')",
                );

                this.options = (response.value || [])
                    .filter((option) => option.type === this.lookupType)
                    .map((option) => {
                        option.editing = false;
                        option.confirmingDelete = false;
                        return option;
                    })
                    .sort((a, b) => {
                        const orderA = a.sortOrder == null ? 9999 : a.sortOrder;
                        const orderB = b.sortOrder == null ? 9999 : b.sortOrder;

                        if (orderA !== orderB) {
                            return orderA - orderB;
                        }

                        return (a.value || "").localeCompare(b.value || "");
                    });
            } catch (e) {
                this.options = [];
            }

            this.loading = false;
        },
        notifyChanged() {
            this.$emit(
                "changed",
                this.options.map((option) => option.value),
            );
        },
        hasDuplicate(value, ignoreId) {
            const name = (value || "").trim().toLowerCase();

            return this.options.some(
                (option) =>
                    option.id !== ignoreId &&
                    (option.value || "").trim().toLowerCase() === name,
            );
        },
        async addValue() {
            if (!this.$refs.newValue.validate(true)) {
                return;
            }

            const value = this.newValue.trim();

            if (this.hasDuplicate(value)) {
                this.$notify({
                    text: "Type already exists",
                    group: "notification",
                    type: "error",
                });
                return;
            }

            const sortOrder =
                this.options.reduce(
                    (max, option) =>
                        option.sortOrder != null && option.sortOrder > max
                            ? option.sortOrder
                            : max,
                    0,
                ) + 1;

            try {
                this.processing = true;

                await this.$http.post("api/smLookup/save", [
                    {
                        id: 0,
                        type: this.lookupType,
                        value: value,
                        sortOrder: sortOrder,
                    },
                ]);

                this.newValue = "";
                this.$refs.newValue.resetValidation();
                await this.load();
                this.notifyChanged();

                this.$notify({
                    text: "Type Added",
                    group: "notification",
                    type: "success",
                });
            } catch (e) {
                // axios plugin already notifies
            }

            this.processing = false;
        },
        editOption(option) {
            this.options.forEach((item) => {
                item.editing = false;
                item.confirmingDelete = false;
            });
            option.originalValue = option.value;
            option.originalSortOrder = option.sortOrder;
            option.editing = true;
        },
        cancelEdit() {
            this.load();
        },
        async saveOption(option) {
            const field = this.$refs.optionValue;
            const input = Array.isArray(field) ? field[0] : field;

            if (input && !input.validate(true)) {
                return;
            }

            const value = (option.value || "").trim();

            if (this.hasDuplicate(value, option.id)) {
                this.$notify({
                    text: "Type already exists",
                    group: "notification",
                    type: "error",
                });
                return;
            }

            const sortOrder =
                option.sortOrder === "" || option.sortOrder == null
                    ? null
                    : Number(option.sortOrder);

            try {
                this.processing = true;

                await this.$http.post("api/smLookup/save", [
                    {
                        id: option.id,
                        type: this.lookupType,
                        value: value,
                        sortOrder: sortOrder,
                        color: option.color,
                    },
                ]);

                await this.load();
                this.notifyChanged();

                this.$notify({
                    text: "Type Saved",
                    group: "notification",
                    type: "success",
                });
            } catch (e) {
                // axios plugin already notifies
            }

            this.processing = false;
        },
        async deleteOption(option) {
            if (option.type !== this.lookupType) {
                return;
            }

            try {
                this.processing = true;

                await this.$http.post("api/smLookup/delete", [option.id]);

                await this.load();
                this.notifyChanged();

                this.$notify({
                    text: "Type Removed",
                    group: "notification",
                    type: "success",
                });
            } catch (e) {
                // axios plugin already notifies
            }

            this.processing = false;
        },
    },
};
</script>
